'use client'

import { useState } from 'react'
import {
  Check,
  MapPin,
  Heart,
  Shield,
  ArrowRight,
  Home,
  Users,
  TreePine,
  Sparkles,
  Layers,
  SunMedium,
  Car,
} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Header } from '@/components/landing/header'
import { Footer } from '@/components/landing/footer'
import { MarketingOpenWagamourButton } from '@/components/landing/MarketingOpenWagamourButton'
import {
  marketingCreamPanel,
  marketingDarkPanel,
  marketingDarkTextPrimary,
  marketingDarkTextSecondary,
  marketingGlassCard,
  marketingIconGradient,
  marketingLightTextPrimary,
  marketingLightTextSecondary,
} from '@/components/landing/marketingV2Surfaces'
import { FAQStructuredData } from '@/components/seo/structured-data'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import {
  MARKETING_PLAY_HOW_IT_WORKS,
  MARKETING_PICKS_HOW_IT_WORKS,
} from '@/components/landing/marketingHowItWorksCopy'
import {
  MARKETING_TRAVEL_HOW_IT_WORKS,
  MARKETING_TRAVEL_PRODUCT,
} from '@/components/landing/marketingTravelCopy'

export default function HowItWorksPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null)

  const faqs = [
    {
      question: 'What is Wagamour?',
      answer: `Wagamour is a Pet Mobility Platform that helps dog parents make better real-world decisions — from when to walk to where to go, who to meet, and what their dog needs. It uses your dog's profile, your location, and real-world conditions to provide clear, actionable guidance — not generic advice.`,
    },
    {
      question: 'How does Wagamour work?',
      answer: `Wagamour combines three inputs:
- Your dog's profile (breed, size, sensitivities, activity level)
- Your location
- Real-world conditions (weather, temperature, environment)

This powers five core experiences:
- Home — walk intelligence and safety guidance
- Play — a trusted circle of compatible dogs, introductions, and playdates
- Parks — live park discovery
- Picks — nearby walking, training, grooming, and veterinary care, plus products from your dog’s profile
- Travel — dog-first journeys (now testing; signal availability varies by vehicle and provider)`,
    },
    {
      question: 'Why does Wagamour need my location?',
      answer: `Location is what makes Wagamour useful.

With location enabled, you get:
- Accurate walk safety insights
- Nearby parks and playdates
- Contextual recommendations based on your environment

Without it, the experience becomes limited and generic.`,
    },
    {
      question: "How does Wagamour use my dog's information?",
      answer: `Your dog's profile helps tailor recommendations to your specific dog — including heat sensitivity, activity level, size, and breed traits.

This improves walk guidance, park recommendations, and product suggestions.

Your data is used only to provide and improve the service — it is not sold to third parties.`,
    },
    {
      question: 'How do product recommendations work?',
      answer: `Wagamour surfaces products based on your dog's profile and current conditions, such as heat, cold, terrain, and activity level.

Some recommendations include affiliate links to trusted retailers. Wagamour may earn a commission at no extra cost to you.

All recommendations are suggestions — you should always use your own judgment.`,
    },
    {
      question: 'Is Wagamour free to use?',
      answer: `Yes. Wagamour is free to start.

Core features available today:
- Walk intelligence (Home)
- Trusted circle and playdates (Play)
- Park discovery (Parks)
- Nearby services, care, and profile-based products (Picks)
- Travel Intelligence (now testing; signal availability varies by vehicle and provider)`,
    },
    {
      question: 'Is Wagamour available everywhere?',
      answer: `Wagamour is currently U.S.-focused.

It works anywhere weather and location data are available, but park data and community signals may be stronger in certain areas.`,
    },
    {
      question: 'Is my data safe?',
      answer: `Yes.

Your data is encrypted, you control what you share, and you can delete your data at any time. Wagamour is designed with privacy and transparency in mind.`,
    },
    {
      question: 'How accurate are the recommendations?',
      answer: `Recommendations are based on real-time conditions and your dog's profile.

They are designed to be helpful guidance — not guarantees. Conditions can change quickly, so you should always use your own judgment.`,
    },
    {
      question: 'Can I use Wagamour without location enabled?',
      answer: `You can, but functionality will be limited.

Without location:
- parks won't be accurate
- playdates won't work properly
- recommendations become generic`,
    },
    {
      question: 'Can I use Wagamour for multiple dogs?',
      answer: `Yes.

Wagamour supports multiple dogs depending on your plan:
- Tail Wagger (Free) — 1 dog profile
- Pack Leader — up to 5 dog profiles

Each dog has its own profile, allowing recommendations to be tailored based on breed, size, sensitivities, and activity level.`,
    },
    {
      question: "What's coming next?",
      answer: `We're building the next layer of Wagamour, including a provider ecosystem (walkers, sitters, groomers, trainers), deeper personalization, and more proactive guidance.

These features are not live yet.`,
    },
    {
      question: 'What makes Wagamour different?',
      answer: `Most pet apps provide information.

Wagamour provides decisions.

It's designed around real-world variability — where conditions change and what's right for your dog depends on context.`,
    },
    {
      question: 'How do I get started?',
      answer: `Open the app at wagamour.com, create your dog's profile, enable location, and start exploring:

Home → Play → Parks → Picks → Travel`,
    },
  ]

  const productSections = [
    {
      id: 'home',
      nav: 'Home',
      icon: Home,
      title: 'Walk intelligence',
      body: 'Start with Home to understand the moment—whether it’s safe to go out and what to watch for before you even grab the leash.',
    },
    {
      id: 'play',
      nav: MARKETING_PLAY_HOW_IT_WORKS.label,
      icon: Users,
      title: MARKETING_PLAY_HOW_IT_WORKS.heading,
      body: MARKETING_PLAY_HOW_IT_WORKS.body,
    },
    {
      id: 'parks',
      nav: 'Parks',
      icon: TreePine,
      title: 'Discovery',
      body: 'Parks helps you choose where to go—based on what’s nearby, what fits your dog, and what the conditions look like right now.',
    },
    {
      id: 'picks',
      nav: MARKETING_PICKS_HOW_IT_WORKS.label,
      icon: Sparkles,
      title: MARKETING_PICKS_HOW_IT_WORKS.heading,
      body: MARKETING_PICKS_HOW_IT_WORKS.body,
    },
    {
      id: 'travel',
      nav: MARKETING_TRAVEL_HOW_IT_WORKS.label,
      icon: Car,
      title: MARKETING_TRAVEL_HOW_IT_WORKS.heading,
      body: MARKETING_TRAVEL_HOW_IT_WORKS.body,
      status: MARKETING_TRAVEL_HOW_IT_WORKS.status,
    },
  ] as const

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#07060A_0%,#18141e_18%,#FFF8EF_18%,#FFF8EF_100%)]">
      <FAQStructuredData faqs={faqs} />
      <Header />
      <div className="container mx-auto max-w-5xl px-4 pb-16 pt-24 md:pt-12">
        {/* 1. Hero */}
        <header className={cn(marketingDarkPanel, 'mb-20 px-6 py-16 text-center text-white md:py-20')}>
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_20%,rgba(255,255,255,0.1),transparent_58%)]"
            aria-hidden
          />
          <div className="relative z-10 mx-auto max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.22em] text-[#FDE68A]">
              The Pet Mobility Platform
            </p>
            <h1 className={cn('mb-5 font-heading text-4xl font-bold md:text-5xl', marketingDarkTextPrimary)}>
              How Wagamour works
            </h1>
            <p className={cn('text-xl leading-relaxed', marketingDarkTextSecondary)}>
              Wagamour turns your dog, your location, and what’s happening right
              now into clear decisions—so you always know what to do next.
            </p>
          </div>
        </header>

        {/* 2. System explanation */}
        <section className="mb-24" aria-labelledby="system-heading">
          <h2 id="system-heading" className="sr-only">
            How the system fits together
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className={cn(marketingGlassCard, 'p-8')}>
              <div className={cn('mb-5 flex h-12 w-12 items-center justify-center rounded-2xl', marketingIconGradient)}>
                <SunMedium className="h-6 w-6" aria-hidden />
              </div>
              <h3 className={cn('mb-3 font-heading text-xl font-bold', marketingLightTextPrimary)}>
                Pet Comfort Intelligence™
              </h3>
              <p className={cn('leading-relaxed', marketingLightTextSecondary)}>
                Wagamour combines what it knows about your dog with real-time
                context—heat, surfaces, weather, time of day, and location—to
                surface the right decision for the moment.
              </p>
            </Card>
            <Card className={cn(marketingGlassCard, 'p-8')}>
              <div className={cn('mb-5 flex h-12 w-12 items-center justify-center rounded-2xl', marketingIconGradient)}>
                <Layers className="h-6 w-6" aria-hidden />
              </div>
              <h3 className={cn('mb-3 font-heading text-xl font-bold', marketingLightTextPrimary)}>
                Location layer
              </h3>
              <p className={cn('leading-relaxed', marketingLightTextSecondary)}>
                Your location connects everything to the real world—what the
                conditions are, what’s nearby, and what actually makes sense
                right now.
              </p>
            </Card>
          </div>
        </section>

        {/* 3. Product flow (order: Home → Play → Parks → Picks → Travel) */}
        <section className="mb-24" aria-labelledby="product-flow-heading">
          <div className="text-center mb-12">
            <h2
              id="product-flow-heading"
              className={cn('mb-3 font-heading text-3xl font-bold', marketingLightTextPrimary)}
            >
              The app, in order
            </h2>
            <p className={cn('mx-auto max-w-2xl', marketingLightTextSecondary)}>
              The app follows how real decisions happen—first you check, then
              you connect, then you choose where to go, you act, and you plan
              the journey.
            </p>
          </div>
          <div className="space-y-6">
            {productSections.map((section, i) => {
              const Icon = section.icon
              return (
                <Card
                  key={section.id}
                  id={section.id}
                  className={cn(marketingGlassCard, 'scroll-mt-28 p-6 md:p-8')}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F6B443]/14 to-[#FF7FA4]/12 text-[#C2410C]">
                      <Icon className="h-7 w-7" aria-hidden />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#9A5B1A]">
                          {section.nav}
                        </p>
                        {'status' in section && section.status ? (
                          <span className="rounded-full border border-[#E8CFAE] bg-[#FFF6E8] px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[#9A5B1A]">
                            {section.status}
                          </span>
                        ) : null}
                      </div>
                      <h3 className={cn('mb-2 font-heading text-xl font-bold', marketingLightTextPrimary)}>
                        {section.title}
                      </h3>
                      <p className={cn('leading-relaxed', marketingLightTextSecondary)}>
                        {section.body}
                      </p>
                    </div>
                    <div
                      className="select-none font-heading text-3xl font-bold text-[#E8CFAE] sm:pt-1"
                      aria-hidden
                    >
                      {String(i + 1).padStart(2, '0')}
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
          <p
            className={cn(
              'mx-auto mt-8 max-w-xl text-center text-sm font-medium leading-relaxed',
              marketingLightTextSecondary
            )}
          >
            {MARKETING_TRAVEL_PRODUCT.disclosure}
          </p>
          <p
            className={cn(
              'mx-auto mt-4 max-w-xl text-center text-sm leading-relaxed',
              marketingLightTextSecondary
            )}
          >
            {MARKETING_TRAVEL_PRODUCT.support}
          </p>
        </section>

        {/* 4. Location */}
        <section className="mb-24" aria-labelledby="location-heading">
          <div className={cn(marketingCreamPanel, 'p-8 md:p-12')}>
            <div className="flex flex-col md:flex-row md:items-start gap-6 mb-10">
              <div className={cn('flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl', marketingIconGradient)}>
                <MapPin className="h-7 w-7" aria-hidden />
              </div>
              <div>
                <h2
                  id="location-heading"
                  className={cn('mb-3 font-heading text-2xl font-bold md:text-3xl', marketingLightTextPrimary)}
                >
                  Why location matters
                </h2>
                <p className={cn('text-lg leading-relaxed', marketingLightTextSecondary)}>
                  Wagamour isn’t generic advice—it works because it’s tied to
                  where you are and what’s happening right now.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h3 className={cn('mb-3 font-semibold', marketingLightTextPrimary)}>
                  With location, you get
                </h3>
                <ul className={cn('space-y-3', marketingLightTextSecondary)}>
                  <li className="flex gap-3">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F6B443]/18">
                      <Check className="h-3.5 w-3.5 text-[#C2410C]" />
                    </span>
                    Walk insight based on real local conditions
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F6B443]/18">
                      <Check className="h-3.5 w-3.5 text-[#C2410C]" />
                    </span>
                    Parks and discovery based on where you actually are
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F6B443]/18">
                      <Check className="h-3.5 w-3.5 text-[#C2410C]" />
                    </span>
                    Play connections with context for fit, distance, and timing
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F6B443]/18">
                      <Check className="h-3.5 w-3.5 text-[#C2410C]" />
                    </span>
                    Nearby walking, training, grooming, and vet care—plus products from your dog’s profile
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F6B443]/18">
                      <Check className="h-3.5 w-3.5 text-[#C2410C]" />
                    </span>
                    Location-based outside conditions when using Travel
                  </li>
                </ul>
              </div>
              <div>
                <h3 className={cn('mb-3 font-semibold', marketingLightTextPrimary)}>Without it</h3>
                <ul className={cn('space-y-3', marketingLightTextSecondary)}>
                  <li className="flex gap-3">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FF7FA4]/14">
                      <Shield className="h-3.5 w-3.5 text-[#C2410C]" />
                    </span>
                    Less accurate guidance
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FF7FA4]/14">
                      <Shield className="h-3.5 w-3.5 text-[#C2410C]" />
                    </span>
                    Parks and Play lose real-world relevance
                  </li>
                </ul>
                <p className={cn('mt-6 text-sm', marketingLightTextSecondary)}>
                  You can change location permission anytime in your device
                  settings. Details are in our{' '}
                  <Link href="/privacy" className="text-[#C2410C] underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Getting started */}
        <section className="mb-24" aria-labelledby="steps-heading">
          <div className="text-center mb-12">
            <h2
              id="steps-heading"
              className={cn('mb-3 font-heading text-3xl font-bold', marketingLightTextPrimary)}
            >
              Getting started
            </h2>
            <p className={cn('mx-auto max-w-xl', marketingLightTextSecondary)}>
              Three simple steps—then just use the app as your day unfolds.
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            {[
              {
                step: '1',
                title: 'Open Wagamour',
                copy: 'Open Wagamour from your browser or install it for quick access.',
                icon: Heart,
              },
              {
                step: '2',
                title: 'Allow location',
                copy: 'Enable location so everything reflects your real environment.',
                icon: MapPin,
              },
              {
                step: '3',
                title: 'Add your dog’s basics',
                copy: 'Add your dog’s basics so recommendations and matches actually fit.',
                icon: Sparkles,
              },
            ].map((item) => {
              const StepIcon = item.icon
              return (
                <div
                  key={item.step}
                  className={cn(marketingGlassCard, 'p-6 text-center')}
                >
                  <div className={cn('mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl', marketingIconGradient)}>
                    <StepIcon className="h-8 w-8" aria-hidden />
                  </div>
                  <span className="mb-2 inline-block text-xs font-bold uppercase tracking-wide text-[#9A5B1A]">
                    Step {item.step}
                  </span>
                  <h3 className={cn('mb-2 text-lg font-bold', marketingLightTextPrimary)}>
                    {item.title}
                  </h3>
                  <p className={cn('text-sm leading-relaxed', marketingLightTextSecondary)}>
                    {item.copy}
                  </p>
                </div>
              )
            })}
          </div>
          <div className="text-center mt-12">
            <MarketingOpenWagamourButton label="Open app" />
          </div>
        </section>

        {/* 6. Available now vs coming next */}
        <section className="mb-24" aria-labelledby="roadmap-heading">
          <h2
            id="roadmap-heading"
            className={cn('mb-10 text-center font-heading text-3xl font-bold', marketingLightTextPrimary)}
          >
            Available now · Coming next
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className={cn(marketingGlassCard, 'p-8')}>
              <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-[#9A5B1A]">
                Available now
              </h3>
              <ul className={cn('space-y-3', marketingLightTextSecondary)}>
                <li className="flex gap-2">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C2410C]" />
                  Home — walk intelligence and comfort guidance
                </li>
                <li className="flex gap-2">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C2410C]" />
                  Play — trusted circle, compatible dogs, and playdates
                </li>
                <li className="flex gap-2">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C2410C]" />
                  Parks — discovery with conditions and community signals
                </li>
                <li className="flex gap-2">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C2410C]" />
                  Picks — nearby walking, training, grooming, and veterinary care, plus products from your dog’s profile
                </li>
                <li className="flex gap-2">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C2410C]" />
                  Travel — dog-first journeys (now testing)
                </li>
              </ul>
            </Card>
            <Card className={cn(marketingCreamPanel, 'p-8')}>
              <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-[#9A5B1A]">
                Coming next
              </h3>
              <ul className={cn('space-y-3', marketingLightTextSecondary)}>
                <li className="flex gap-2">
                  <ArrowRight className="mt-0.5 h-5 w-5 shrink-0 text-[#F6B443]" />
                  Provider tools and coordination (waitlist on the homepage)
                </li>
                <li className="flex gap-2">
                  <ArrowRight className="mt-0.5 h-5 w-5 shrink-0 text-[#F6B443]" />
                  Pet care services and booking—not live in app today
                </li>
                <li className="flex gap-2">
                  <ArrowRight className="mt-0.5 h-5 w-5 shrink-0 text-[#F6B443]" />
                  More mobility features as we ship
                </li>
              </ul>
              <p className={cn('mt-6 text-sm', marketingLightTextSecondary)}>
                We’ll share new features as they become available.
              </p>
            </Card>
          </div>
        </section>

        {/* 7. FAQ */}
        <section aria-labelledby="faq-heading">
          <h2
            id="faq-heading"
            className={cn('mb-12 text-center font-heading text-3xl font-bold', marketingLightTextPrimary)}
          >
            Frequently asked questions
          </h2>
          <div className="space-y-4 max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <Card
                key={faq.question}
                className={cn(marketingGlassCard, 'overflow-hidden rounded-[24px] p-0')}
              >
                <button
                  type="button"
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="flex w-full items-center justify-between p-6 text-left transition-colors hover:bg-[#FFF6E8]/80"
                  aria-expanded={openFAQ === index}
                >
                  <h3 className={cn('pr-4 text-lg font-semibold', marketingLightTextPrimary)}>
                    {faq.question}
                  </h3>
                  <ArrowRight
                    className={`h-5 w-5 shrink-0 text-[#C2410C] transition-transform ${
                      openFAQ === index ? 'rotate-90' : ''
                    }`}
                  />
                </button>
                {openFAQ === index && (
                  <div className="border-t border-transparent px-6 pb-6 pt-0">
                    <p className={cn('whitespace-pre-line border-t border-[#E8CFAE]/65 pt-4 leading-relaxed', marketingLightTextSecondary)}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  )
}
